import type { Drill, Hint } from "./types.ts";
import { HARNESS_KIT } from "./harness-kit.ts";

const starter = `function get(object: unknown, path: string, fallback?: unknown): unknown {
  // TODO
  return fallback;
}

function set(object: Record<string, unknown>, path: string, value: unknown): void {
  // TODO
}
`;

const solution = `// "a.b[0].c" and "a.b.0.c" both become ["a","b","0","c"]. Doing this once, in
// one place, is what keeps get and set agreeing on what a path means.
function toSegments(path: string): string[] {
  return path
    .replace(/\\[(\\w+)\\]/g, ".$1")
    .split(".")
    .filter((segment) => segment !== "");
}

function get(object: unknown, path: string, fallback?: unknown): unknown {
  let current = object;
  for (const segment of toSegments(path)) {
    if (current === null || current === undefined) return fallback;
    current = (current as Record<string, unknown>)[segment];
  }
  // Only a genuinely missing value falls back. A stored null is a value someone
  // chose, and replacing it with the default silently rewrites their data.
  return current === undefined ? fallback : current;
}

function set(object: Record<string, unknown>, path: string, value: unknown): void {
  const segments = toSegments(path);
  if (segments.length === 0) return;

  let current: Record<string, unknown> = object;
  for (let i = 0; i < segments.length - 1; i++) {
    const key = segments[i] as string;
    const next = current[key];
    if (next === null || typeof next !== "object") {
      // The NEXT segment decides the container: a numeric key means the thing
      // being indexed is an array. Creating an object for "0" gives you
      // { "0": ... }, which stringifies wrong and has no array methods.
      current[key] = /^\\d+$/.test(segments[i + 1] as string) ? [] : {};
    }
    current = current[key] as Record<string, unknown>;
  }
  current[segments[segments.length - 1] as string] = value;
}`;

const harness = HARNESS_KIT + `
if (typeof get !== "function" || typeof set !== "function") {
  throw new Error("get and set must both be defined. Keep the names as given.");
}

const __fixture = function () {
  return {
    user: {
      name: "Ada",
      roles: [{ name: "admin" }, { name: "editor" }],
      meta: { active: null }
    }
  };
};

const __tests = [
  {
    name: "reads a nested value",
    about: "The basic dotted path.",
    run: function () {
      __expectExact("get(o, 'user.name')", function () { return get(__fixture(), "user.name"); }, "Ada");
    }
  },
  {
    name: "reads through array indexes",
    about: "Bracket notation is how paths are written in the wild.",
    run: function () {
      __expectExact("get(o, 'user.roles[1].name')", function () {
        return get(__fixture(), "user.roles[1].name");
      }, "editor");
    }
  },
  {
    name: "returns the fallback for a missing path",
    about: "The reason to use get rather than optional chaining everywhere.",
    run: function () {
      __expectExact("get(o, 'user.nope.deep', 'none')", function () {
        return get(__fixture(), "user.nope.deep", "none");
      }, "none");
    }
  },
  {
    name: "writes a nested value",
    about: "The write counterpart, on a path that already exists.",
    run: function () {
      const o = __fixture();
      set(o, "user.name", "Grace");
      __expectExact("set(o, 'user.name', 'Grace')", function () { return o.user.name; }, "Grace");
    }
  },
  {
    name: "does not blow up part-way down a missing path",
    hidden: true,
    about: "Indexing into undefined throws. Reading a path that stops existing half way is the normal case, not an exceptional one.",
    run: function () {
      __expectExact("get({}, 'a.b.c.d.e')", function () { return get({}, "a.b.c.d.e", "safe"); }, "safe");
    }
  },
  {
    name: "a stored null is returned, not replaced by the fallback",
    hidden: true,
    about: "null is a value somebody chose to store. Treating it as missing silently rewrites their data, and the bug surfaces far from here.",
    run: function () {
      __expectExact("get(o, 'user.meta.active', 'DEFAULT')", function () {
        return get(__fixture(), "user.meta.active", "DEFAULT");
      }, null);
    }
  },
  {
    name: "creates missing intermediate objects on set",
    hidden: true,
    about: "set has to build the path it is given, or it only works on shapes that already exist.",
    run: function () {
      const o = {};
      set(o, "a.b.c", 1);
      __expectExact("set({}, 'a.b.c', 1)", function () { return o; }, { a: { b: { c: 1 } } });
    }
  },
  {
    name: "a numeric segment creates an array, not an object",
    hidden: true,
    about: "Creating {} for a numeric key gives { '0': x }, which has no array methods, no length, and serialises to a different shape. It looks right in a console and is wrong everywhere else.",
    run: function () {
      const o = {};
      set(o, "items[0].id", 7);
      __expectExact("set({}, 'items[0].id', 7): is items an array", function () {
        return Array.isArray(o.items);
      }, true);
      __expectExact("... value", function () { return o.items[0].id; }, 7);
    }
  },
  {
    name: "does not clobber an existing intermediate",
    hidden: true,
    about: "Overwriting whatever is on the way to the target destroys its siblings. set writes one leaf.",
    run: function () {
      const o = { user: { name: "Ada", age: 36 } };
      set(o, "user.age", 37);
      __expectExact("set existing sibling intact", function () { return o.user; }, { name: "Ada", age: 37 });
    }
  },
  {
    name: "bracket and dot notation mean the same thing",
    hidden: true,
    about: "If get and set parse paths differently, set writes somewhere get cannot read. Normalise once, in one place.",
    run: function () {
      const o = { a: [{ b: 1 }] };
      __expectExact("get(o, 'a[0].b')", function () { return get(o, "a[0].b"); }, 1);
      __expectExact("get(o, 'a.0.b')", function () { return get(o, "a.0.b"); }, 1);
    }
  },
  {
    name: "a single-segment path works",
    hidden: true,
    about: "The loop that walks to the parent has nothing to walk. An off-by-one here fails the simplest possible call.",
    run: function () {
      const o = {};
      set(o, "top", 1);
      __expectExact("set(o, 'top', 1)", function () { return o; }, { top: 1 });
      __expectExact("get(o, 'top')", function () { return get(o, "top"); }, 1);
    }
  },
  {
    name: "reading from null or undefined returns the fallback",
    hidden: true,
    about: "Callers pass whatever an API gave them, which is sometimes nothing at all.",
    run: function () {
      __expectExact("get(null, 'a.b', 'x')", function () { return get(null, "a.b", "x"); }, "x");
      __expectExact("get(undefined, 'a', 'x')", function () { return get(undefined, "a", "x"); }, "x");
    }
  }
];
`;

const hints: Hint[] = [
  {
    label: "Nudge",
    body: "The default is only for a genuinely missing value. Ask what ?? does to a null that somebody deliberately stored. Then notice that get and set have to agree on what a path means, which is one function, written once.",
    code: `// "a.b[0].c" and "a.b.0.c" both become ["a","b","0","c"]. Doing this in one
// place is what keeps get and set from disagreeing about where a value lives.
function toSegments(path: string): string[] {
  return path
    .replace(/\\[(\\w+)\\]/g, ".$1")
    .split(".")
    .filter((segment) => segment !== "");
}`,
  },
  {
    label: "Approach",
    body: "get walks the segments, bailing to the fallback the moment it is standing on nothing, and falls back at the end only when the value is undefined. set walks to the parent of the last segment, creating containers on the way, then writes one leaf.",
    code: `function get(object: unknown, path: string, fallback?: unknown): unknown {
  let current = object;
  for (const segment of toSegments(path)) {
    if (current === null || current === undefined) return fallback;   // indexing undefined throws
    current = (current as Record<string, unknown>)[segment];
  }
  // Only a genuinely missing value falls back. A stored null was somebody's choice.
  return current === undefined ? fallback : current;
}

// set: walk to the parent, then assign. Note it stops one short of the end.
for (let i = 0; i < segments.length - 1; i++) {
  const key = segments[i] as string;
  const next = current[key];
  if (next === null || typeof next !== "object") {
    current[key] = /* ... a container. Which one? See the next rung. */ {};
  }
  current = current[key] as Record<string, unknown>;
}
current[segments[segments.length - 1] as string] = value;`,
  },
  {
    label: "The trap",
    body: "Which container to create depends on the NEXT segment, not the current one: a numeric segment means the thing being indexed is an array. Create an object instead and you get { \"0\": ... }, which has no length, no array methods, and serialises to a different shape. It looks right in a console and is wrong everywhere else.",
  },
];

export const pathAccess: Drill = {
  id: "path-access",
  track: "typescript",
  title: "Get and set by path",
  pattern: "path-access",
  difficulty: "medium",
  basePoints: 250,
  timeBudgetMs: 25 * 60 * 1000,
  statement: `Form libraries, config loaders and i18n all need to read and write a value nested somewhere inside an object, addressed by a string. Implement \`get\` and \`set\`.

\`\`\`ts
function get(object: unknown, path: string, fallback?: unknown): unknown;
function set(object: Record<string, unknown>, path: string, value: unknown): void;
\`\`\`

Paths use dots and brackets: \`"user.roles[0].name"\`.

## Requirements

- **\`get\` walks the path** and returns the fallback if any step is missing.
- **\`get\` returns a stored \`null\`**, rather than treating it as missing.
- **\`set\` creates the intermediates** it needs.
- **A numeric segment creates an array.**
- **\`set\` does not disturb anything else**, including siblings of the target.
- Bracket and dot notation address the same thing.

## Example

\`\`\`ts
const user = { name: "Ada", roles: [{ name: "admin" }] };

get(user, "roles[0].name");            // "admin"
get(user, "roles[9].name", "none");    // "none"

const draft = {};
set(draft, "contact.emails[0]", "a@b.c");
// draft is now { contact: { emails: ["a@b.c"] } }   ← an array, not { "0": ... }
\`\`\`

## Where this gets interesting

The fallback rule is a decision, not a detail. \`??\` and a truthiness check both treat a stored \`null\` as absent, so a value someone deliberately saved gets silently replaced by your default, and the damage shows up somewhere else entirely. Only a genuinely missing value should fall back.

On the write side, the trap is what container to create. \`"items[0].id"\` needs \`items\` to be an **array**. Create an object and you get \`{ "0": { id: 7 } }\`, which has no \`length\`, no array methods, and serialises to a different shape. It looks fine in a console.

And both functions have to agree on what a path means, or \`set\` writes somewhere \`get\` cannot read.`,
  hints,
  starter,
  harness,
  solution,
  complexity: "O(d) in the depth of the path for both, with one pass to split the path into segments.",
};
